/** @format */

import {
  ATC_BUTTON_TEXTS,
  ATC_PICKUP_STORE_TEXTS,
  BUTTON_ACTIONS,
  BUTTON_LABELS,
  BUTTON_TEXTS,
  CHANGE_STORE_BUTTON_TEXTS,
  LABEL_TEXTS,
  LOW_STOCK_MESSAGE,
  NEARBY_STORES_LINK_CONTENT,
  STORE_CLOSED_MESSAGE,
} from './fulfillment-buttons.constants';
import { BOPIS_AVAILABLE, OPEN_STORE_STATUS, StoreStatus } from '@shared/data-access/enums';
import { FulfillmentState } from '@pdp/data-access/interfaces';
import {
  HeaderStoreSelection,
  ISkuLocation,
  ProductSku,
  ProductStyle,
  SddInventory,
  SkuDescriptiveAttribute,
} from '@shared/data-access/interfaces';
import {
  createFeatureSelector,
  createSelector,
  createSelectorFactory,
  defaultMemoize,
} from '@ngrx/store';
import {
  selectAllProductStyle,
  selectDefiningAttributesLength,
  selectIsComingSoon,
  selectIsEcodePreSale,
  selectIsResolvedSkuInSkusWithInventory,
  selectIsResolvedSkuNotifyMeEligible,
  selectKillswitch,
  selectNearbyAvailableStoreCount,
  selectPreSaleInfo,
  selectResolvedSku,
  selectResolvedSkuBopisInventory,
  selectResolvedSkuEcommInventory,
  selectResolvedSkuSDDInventory,
  selectSelectedPersonalizationOptions,
  selectSkusWithInventory,
  selectcustomProductData,
} from '@pdp/data-access/state';
import {
  selectAvailableAttributes,
  selectSelectedAttributeOptions,
} from '@pdp/attributes/data-access';
import {
  selectPickupTimeText,
  selectUserStoreInfo,
  selectUserStoreNameTitleCase,
  selectUserZipCode,
} from '@state/data-access';

import { FulfillmentButtonInterface } from '../../interfaces/fulfillment-button.interface';
import { StoreStockAmounts } from '../../interfaces/store-pickup.interface';
import { selectAtcFulfillmentOption, selectAtcUserInputQty } from '../add-to-cart.selectors';

/**
 * @description Same Day Delivery Fulfillment Button
 * Handles each different scenario for fulfillment functionality
 */

const getFullFillmentButtonTextInStockSDD = (
  descriptiveAttributes: SkuDescriptiveAttribute[],
  nonDynamicText: string,
  pickupTimeText: string,
): string => {
  const isBike = descriptiveAttributes.some(
    (attr) => attr.name === 'Product Type' && attr.value === 'Bikes',
  );

  if (isBike) {
    return nonDynamicText;
  }

  return pickupTimeText;
};

export const selectFulfillmentState = createFeatureSelector<FulfillmentState>('fulfillment');

export const selectShipMode = createSelector(selectFulfillmentState, (state) => {
  return state?.fulfillmentButtonsReducer.shipMode;
});

export const selectNearbyStoresButtonEnabledFlag = createSelector(
  selectFulfillmentState,
  (state) => state?.fulfillmentButtonsReducer.nearbyStoresButtonEnabled,
);

export const selectSameDayDeliveryButtonText = createSelector(
  selectResolvedSku,
  selectUserZipCode,
  selectResolvedSkuSDDInventory,
  selectAtcUserInputQty,
  (
    resolvedSku: ProductSku,
    userZipCode: string,
    sddInventory: SddInventory,
    quantityInput: number,
  ): FulfillmentButtonInterface => {
    if (!userZipCode || userZipCode === 'null') {
      const fulfillmentButtonContent: FulfillmentButtonInterface = {
        showZipCodeButton: true,
        fulfillmentButtonDisabledClass: false,
        addToCartButtonDisabled: false,
      };
      if (resolvedSku === null) {
        fulfillmentButtonContent.addToCartButtonText = ATC_BUTTON_TEXTS.atc;
        fulfillmentButtonContent.buttonAction = BUTTON_ACTIONS.selectOptions;
        fulfillmentButtonContent.fulfillmentButtonText = 'Select product options';
        fulfillmentButtonContent.showZipCodeButton = false;
      } else {
        fulfillmentButtonContent.addToCartButtonText = ATC_BUTTON_TEXTS.noDeliveryLocation;
        fulfillmentButtonContent.fulfillmentButtonText = '';
        fulfillmentButtonContent.fulfillmentLabelTextLine1 = 'Same Day Delivery to';
      }
      return fulfillmentButtonContent;
    }
    if (resolvedSku === null) {
      return {
        fulfillmentButtonText: BUTTON_TEXTS.noSkuResolved,
        fulfillmentButtonDisabledClass: false,
        fulfillmentLabelTextLine1: LABEL_TEXTS.sdd,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        buttonAction: BUTTON_ACTIONS.selectOptions,
        addToCartButtonDisabled: false,
      };
    }
    // In stock product available and same day delivery returns data and has inventory
    if (sddInventory && sddInventory.sameDayQuantity >= quantityInput) {
      return {
        fulfillmentButtonDisabledClass: false,
        fulfillmentLabelTextLine1: LABEL_TEXTS.sdd,
        sddAvailable: true,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        addToCartButtonDisabled: false,
        ...(sddInventory.sameDayQuantity < 10 && {
          lowStockMessage: LOW_STOCK_MESSAGE(sddInventory.sameDayQuantity),
        }),
      };
      // Product is not available for SDD
    }
    return {
      fulfillmentButtonText: BUTTON_TEXTS.notAvailableTo,
      fulfillmentButtonDisabledClass: true,
      showZipCodeButton: true,
      fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableToShip,
      addToCartButtonText: ATC_BUTTON_TEXTS.atc,
      addToCartButtonDisabled: true,
      notAvailable: true,
      isLowerQuantityAvailable: sddInventory?.sameDayQuantity > 0,
    };
  },
);

export const selectIsShipToMeAvailable = createSelector(
  selectResolvedSkuEcommInventory,
  selectAtcUserInputQty,
  (inventory: ISkuLocation, quantityInput: number): boolean => {
    const amountAvailableToShip = Number.parseInt(inventory?.atsqty, 10);
    return !!inventory && amountAvailableToShip >= quantityInput;
  },
);

export const selectCanBopis = createSelectorFactory(defaultMemoize)(
  selectResolvedSkuBopisInventory,
  selectUserStoreInfo,
  selectAtcUserInputQty,
  (
    inventory: ISkuLocation | null,
    userStoreInfo: HeaderStoreSelection,
    quantityInput: number,
  ): boolean => {
    const amountAvailableForBopis = Number.parseInt(inventory?.atsqty, 10);
    const storeIsOpenForBopis = BOPIS_AVAILABLE.includes(userStoreInfo?.status as StoreStatus);
    return amountAvailableForBopis >= quantityInput && storeIsOpenForBopis;
  },
);

export const selectCanShipToStore = createSelectorFactory(defaultMemoize)(
  selectResolvedSkuBopisInventory,
  selectUserStoreInfo,
  selectAtcUserInputQty,
  (
    inventory: ISkuLocation | null,
    userStoreInfo: HeaderStoreSelection,
    quantityInput: number,
  ): boolean => {
    const amountAvailableToShipToStore = Number.parseInt(inventory?.boplqty, 10);
    const storeIsOpenForBopis = BOPIS_AVAILABLE.includes(userStoreInfo?.status as StoreStatus);
    return amountAvailableToShipToStore >= quantityInput && storeIsOpenForBopis;
  },
);

export const selectIsBopisOrBoplAvailable = createSelector(
  selectCanBopis,
  selectCanShipToStore,
  (canBopis: boolean, canShipToStore: boolean): boolean => canBopis || canShipToStore,
);

export const selectShipToMeButtonText = createSelector(
  selectResolvedSku,
  selectResolvedSkuEcommInventory,
  selectPreSaleInfo,
  selectIsEcodePreSale,
  selectAtcUserInputQty,
  selectIsResolvedSkuNotifyMeEligible,
  selectIsShipToMeAvailable,
  (
    resolvedSku: ProductSku,
    inventory: ISkuLocation,
    preSaleInfo: { isPreSale: boolean; preSaleDate: string; isPreSaleCurrent: boolean },
    isEcodePreSale: boolean,
    quantityInput: number,
    isResolvedSkuNotifyMeEligible: boolean,
    isShipToMeAvailable: boolean,
  ): FulfillmentButtonInterface => {
    const amountAvailableToShip = Number.parseInt(inventory?.atsqty, 10);
    // Not sku resolved
    if (resolvedSku === null) {
      // Show pre-order text if ecode pre-sale, otherwise show no sku resolved text
      const shouldShowPreOrder = isEcodePreSale;

      return {
        fulfillmentButtonText: shouldShowPreOrder
          ? BUTTON_TEXTS.preOrder
          : BUTTON_TEXTS.noSkuResolved,
        fulfillmentLabelTextLine1: LABEL_TEXTS.shipTo,
        fulfillmentButtonDisabledClass: false,
        addToCartButtonText: shouldShowPreOrder ? ATC_BUTTON_TEXTS.preOrder : ATC_BUTTON_TEXTS.atc,
        buttonAction: BUTTON_ACTIONS.selectOptions,
        addToCartButtonDisabled: false,
      };
    }
    if (isShipToMeAvailable) {
      // Only use pre-sale logic if killswitch is enabled
      const isPreSaleCurrentlyActiveOnSku = preSaleInfo.isPreSaleCurrent;

      return {
        fulfillmentButtonText: isPreSaleCurrentlyActiveOnSku
          ? BUTTON_TEXTS.preOrder
          : BUTTON_TEXTS.available,
        fulfillmentLabelTextLine1: LABEL_TEXTS.shipTo,
        fulfillmentButtonDisabledClass: false,
        showEDD: true,
        addToCartButtonText: preSaleInfo.isPreSaleCurrent
          ? ATC_BUTTON_TEXTS.preOrder
          : ATC_BUTTON_TEXTS.atc,
        addToCartButtonDisabled: false,
        ...(amountAvailableToShip < 10 && {
          lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToShip),
        }),
      };
    }
    // Product is not available for Ship To Me
    return {
      fulfillmentButtonText: BUTTON_TEXTS.notAvailable,
      fulfillmentButtonDisabledClass: true,
      fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableToShip,
      addToCartButtonText: isResolvedSkuNotifyMeEligible
        ? ATC_BUTTON_TEXTS.notifyMe
        : ATC_BUTTON_TEXTS.atc,
      addToCartButtonDisabled: !isResolvedSkuNotifyMeEligible,
      showOutOfStockRecs: true,
      isLowerQuantityAvailable: amountAvailableToShip > 0,
      notAvailable: true,
    };
  },
);

const applyStoreClosedState = (
  content: FulfillmentButtonInterface,
  amounts: StoreStockAmounts,
  quantityInput: number,
): void => {
  content.storeClosedMessage = STORE_CLOSED_MESSAGE.temporarilyClosed;
  /**
   * Get the first non-zero amount of stock. Mirrors the order we check, so the amount
   * displayed is the same as if the store wasn't closed.
   */
  const storeClosedLowStock = amounts.bopis || amounts.shipToStore || amounts.inStore;
  if (storeClosedLowStock <= 0) return;

  content.fulfillmentLabelTextLine1 = LABEL_TEXTS.pickupAtStore;
  content.isLowerQuantityAvailable = storeClosedLowStock < quantityInput;
  if (storeClosedLowStock < 10) {
    content.lowStockMessage = LOW_STOCK_MESSAGE(storeClosedLowStock);
  }
};

const buildNotAvailableContent = (
  storeClosed: boolean,
  nearbyStores: number,
  nearbyStoresButtonEnabled: boolean,
  notAvailableATCText: string,
  addToCartButtonDisabled: boolean,
  amounts: StoreStockAmounts,
  quantityInput: number,
  isBopisUiChangesEnabled: boolean,
): FulfillmentButtonInterface => {
  const content: FulfillmentButtonInterface = {
    fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
    fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
    addToCartButtonDisabled,
    showOutOfStockRecs: true,
    isLowerQuantityAvailable: amounts.bopis > 0 || amounts.shipToStore > 0 || amounts.inStore > 0,
    notAvailable: true,
  };

  if (storeClosed) {
    applyStoreClosedState(content, amounts, quantityInput);
  }

  if (nearbyStores > 0) {
    content.fulfillmentButtonDisabledClass = false;
    Object.assign(content, NEARBY_STORES_LINK_CONTENT(nearbyStores, isBopisUiChangesEnabled));
    content.changeStoreButtonDataEm = 'PDP_seeAvailableStores_card';
    content.addToCartButtonText = nearbyStoresButtonEnabled
      ? ATC_BUTTON_TEXTS.nearbyStores
      : notAvailableATCText;
  } else {
    content.fulfillmentButtonText = storeClosed
      ? BUTTON_TEXTS.storeClosed
      : BUTTON_TEXTS.notAvailable;
    content.fulfillmentButtonDisabledClass = true;
    content.addToCartButtonText = notAvailableATCText;
  }

  return content;
};

export const selectNearbyStoresInfo = createSelector(
  selectNearbyAvailableStoreCount,
  selectNearbyStoresButtonEnabledFlag,
  (nearbyStores: number, nearbyStoresButtonEnabled: boolean) => ({
    nearbyStores,
    nearbyStoresButtonEnabled,
  }),
);

export const selectInStorePickupButtonText = createSelectorFactory(defaultMemoize)(
  selectResolvedSku,
  selectUserStoreNameTitleCase,
  selectResolvedSkuBopisInventory,
  selectNearbyStoresInfo,
  selectUserStoreInfo,
  selectAtcUserInputQty,
  selectPickupTimeText,
  selectIsResolvedSkuNotifyMeEligible,
  selectCanBopis,
  selectCanShipToStore,
  selectKillswitch('isBopisUiChangesEnabled'),
  (
    resolvedSku: ProductSku,
    storeName: string,
    inventory: ISkuLocation | null,
    nearbyStoresInfo: { nearbyStores: number; nearbyStoresButtonEnabled: boolean },
    userStoreInfo: HeaderStoreSelection,
    quantityInput: number,
    pickupTimeText: string,
    isResolvedSkuNotifyMeEligible: boolean,
    canBopis: boolean,
    canShipToStore: boolean,
    isBopisUiChangesEnabled: boolean,
  ): FulfillmentButtonInterface => {
    const { nearbyStores, nearbyStoresButtonEnabled: nearbyStoresButtonEnabledFlag } =
      nearbyStoresInfo;
    const amountAvailableForBopis = Number.parseInt(inventory?.atsqty, 10);
    const amountAvailableToShipToStore = Number.parseInt(inventory?.boplqty, 10);
    const amountAvailableToBuyInStore = Number.parseInt(inventory?.isaqty, 10);

    const storeIsOpenForBopis = BOPIS_AVAILABLE.includes(userStoreInfo?.status as StoreStatus);
    const storeIsOpenForFootTraffic = OPEN_STORE_STATUS.includes(
      userStoreInfo?.status as StoreStatus,
    );

    if (resolvedSku === null) {
      return {
        fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
        fulfillmentButtonText: BUTTON_TEXTS.noSkuResolved,
        fulfillmentButtonDisabledClass: false,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        buttonAction: BUTTON_ACTIONS.selectOptions,
        addToCartButtonDisabled: false,
      };
    }
    if (!storeName) {
      return {
        fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
        changeStoreButtonText: CHANGE_STORE_BUTTON_TEXTS.noStoreSelected,
        fulfillmentButtonDisabledClass: false,
        addToCartButtonText: ATC_BUTTON_TEXTS.noStoreSelected,
        addToCartButtonDisabled: false,
      };
    }
    const canBuyInStore = amountAvailableToBuyInStore >= quantityInput && storeIsOpenForFootTraffic;

    if (canBopis) {
      const fulfillmentButtonText = getFullFillmentButtonTextInStockSDD(
        resolvedSku.descriptiveAttributes,
        BUTTON_TEXTS.inStockAtStore(storeName),
        pickupTimeText,
      );

      return {
        fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
        nonDynamicFullfillmentButtonText: BUTTON_TEXTS.inStockAtStore(storeName),
        fulfillmentButtonText: fulfillmentButtonText,
        fulfillmentButtonDisabledClass: false,
        fulfillmentLabelTextLine1: LABEL_TEXTS.pickupAtStore,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        addToCartButtonDisabled: false,
        // only the dynamic pickup-time copy counts as "ready"; the non-dynamic fallback shouldn't get ready styling
        isBopisReady: !!pickupTimeText,
        ...(amountAvailableForBopis < 10 && {
          lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableForBopis),
        }),
        addToCartSuccessText:
          fulfillmentButtonText === pickupTimeText
            ? pickupTimeText
            : ATC_PICKUP_STORE_TEXTS.inStock,
      };
    }
    if (canShipToStore) {
      return {
        fulfillmentButtonLabel: BUTTON_LABELS.shipToStore,
        fulfillmentButtonText: BUTTON_TEXTS.shipToStore,
        fulfillmentLabelTextLine1: LABEL_TEXTS.pickupAtStore,
        fulfillmentButtonDisabledClass: false,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        addToCartButtonDisabled: false,
        ...(amountAvailableToShipToStore < 10 && {
          lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToShipToStore),
        }),
        isBopl: true,
        addToCartSuccessText: ATC_PICKUP_STORE_TEXTS.shipToStore,
        // fulfillmentButtonText above is always the static shipToStore copy, never the dynamic/fallback text, so it's always "ready"
        isBopisReady: true,
      };
    }
    if (canBuyInStore) {
      return {
        fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
        fulfillmentButtonText: BUTTON_TEXTS.purchaseInStore,
        fulfillmentButtonDisabledClass: true,
        fulfillmentLabelTextLine1: LABEL_TEXTS.inStockAtStore,
        addToCartButtonText: ATC_BUTTON_TEXTS.atc,
        addToCartButtonDisabled: true,
        ...(amountAvailableToBuyInStore < 10 && {
          lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToBuyInStore),
        }),
        availableInStoreOnly: true,
      };
    }
    const storeClosed = !storeIsOpenForBopis || !storeIsOpenForFootTraffic;
    const notAvailableATCText = isResolvedSkuNotifyMeEligible
      ? ATC_BUTTON_TEXTS.notifyMe
      : ATC_BUTTON_TEXTS.atc;
    const amounts: StoreStockAmounts = {
      bopis: amountAvailableForBopis,
      shipToStore: amountAvailableToShipToStore,
      inStore: amountAvailableToBuyInStore,
    };

    return buildNotAvailableContent(
      storeClosed,
      nearbyStores,
      nearbyStoresButtonEnabledFlag,
      notAvailableATCText,
      !isResolvedSkuNotifyMeEligible,
      amounts,
      quantityInput,
      isBopisUiChangesEnabled,
    );
  },
);

export const selectOptionNotAvailable = createSelector(
  selectAvailableAttributes,
  selectSelectedAttributeOptions,
  (
    availableAttributes: Record<string, string[]>,
    selectedAttributeOptions: { name: string; value: string }[],
  ) => {
    let optionUnavailable = false;

    if (!selectedAttributeOptions) {
      return false;
    }

    Object.keys(availableAttributes).forEach((attrKey) => {
      const selectedOption = selectedAttributeOptions.find(
        (val) => val.name.toLowerCase() === attrKey.toLowerCase(),
      );

      if (selectedOption && !availableAttributes[attrKey].includes(selectedOption.value)) {
        optionUnavailable = true;
      }
    });

    return optionUnavailable;
  },
);

export const selectShouldShowOutOfStockRecs = createSelector(
  selectShipToMeButtonText,
  selectInStorePickupButtonText,
  (shipToMeText: FulfillmentButtonInterface, inStorePickupText: FulfillmentButtonInterface) =>
    shipToMeText.showOutOfStockRecs && inStorePickupText.showOutOfStockRecs,
);

export const selectIsOosOrUnresolvedSkuSelected = createSelector(
  selectSkusWithInventory,
  selectResolvedSku,
  selectDefiningAttributesLength,
  selectSelectedAttributeOptions,
  selectIsResolvedSkuInSkusWithInventory,
  (
    skusWithInventory,
    resolvedSku,
    definingAttributesLength,
    selectedAttributeOptions,
    isResolvedSkuInSkusWithInventory,
  ): boolean =>
    skusWithInventory.isDataInitialized &&
    definingAttributesLength === selectedAttributeOptions?.length &&
    resolvedSku !== null &&
    (resolvedSku !== undefined || definingAttributesLength > 0) &&
    !isResolvedSkuInSkusWithInventory,
);

/* When isNotifyMeOOSSKUEnabled is on, this is the single source of truth for whether OOS recs show and where they show on the page */
export const selectOosRecommendationsPlacement = createSelector(
  selectKillswitch('isNotifyMeOOSSKUEnabled'),
  selectIsOosOrUnresolvedSkuSelected,
  selectIsResolvedSkuNotifyMeEligible,
  (
    isNotifyMeOOSSKUEnabled,
    isOosOrUnresolvedSkuSelected,
    isResolvedSkuNotifyMeEligible,
  ): 'attributes' | 'below-buttons' | null => {
    if (!isNotifyMeOOSSKUEnabled || !isOosOrUnresolvedSkuSelected) {
      return null;
    }

    return isResolvedSkuNotifyMeEligible ? 'below-buttons' : 'attributes';
  },
);

/* Soft deprecated - remove with isNotifyMeOOSSKUEnabled when the legacy experience is removed */
export const selectShouldShowAlternativeInStockRecs = createSelector(
  selectKillswitch('isNotifyMeOOSSKUEnabled'),
  selectDefiningAttributesLength,
  selectSelectedAttributeOptions,
  selectResolvedSku,
  (
    isNotifyMeOOSSKUEnabled,
    definingAttributesLength,
    selectedAttributeOptions,
    resolvedSku,
  ): boolean =>
    !isNotifyMeOOSSKUEnabled &&
    definingAttributesLength === selectedAttributeOptions?.length &&
    resolvedSku === undefined,
);

export const selectIsInStoreOnlyProduct = createSelector(
  selectAllProductStyle,
  selectIsComingSoon,
  // return whether or not the ecode product is in store only
  (productStyle: ProductStyle | Record<string, never>, isComingSoon: boolean) => {
    return productStyle?.buyable === 0 && !isComingSoon;
  },
);

const selectIsNotifyMeTextForFulfillmentOption = createSelector(
  selectAtcFulfillmentOption,
  selectShipToMeButtonText,
  selectInStorePickupButtonText,
  (
    fulfillmentOption: string,
    shipToMeText: FulfillmentButtonInterface,
    inStorePickupText: FulfillmentButtonInterface,
  ): boolean => {
    if (fulfillmentOption === 'sameDayDelivery') return false;

    const buttonText = fulfillmentOption === 'shipToMe' ? shipToMeText : inStorePickupText;
    return buttonText?.addToCartButtonText === ATC_BUTTON_TEXTS.notifyMe;
  },
);

export const selectIsNotifyMeButtonShown = createSelector(
  selectKillswitch('notifyUserWhenAvailableEnabled'),
  selectIsComingSoon,
  selectAllProductStyle,
  selectcustomProductData,
  selectSelectedPersonalizationOptions,
  selectIsNotifyMeTextForFulfillmentOption,
  (
    notifyUserWhenAvailableEnabled,
    isComingSoon,
    productStyle,
    customProductData,
    selectedPersonalizationOptions,
    isNotifyMeTextForFulfillmentOption,
  ): boolean => {
    if (!notifyUserWhenAvailableEnabled || isComingSoon) return false;
    if (productStyle?.isCustomizable && !customProductData) return false;
    if (productStyle?.isPersonalizable && !selectedPersonalizationOptions?.valid) return false;
    return isNotifyMeTextForFulfillmentOption;
  },
);
